import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhVideoCamera";
import "@phosphor-icons/webcomponents/PhPlus";
import "@phosphor-icons/webcomponents/PhPencil";
import "@phosphor-icons/webcomponents/PhTrash";
import "@phosphor-icons/webcomponents/PhCalendar";
import "@phosphor-icons/webcomponents/PhCircle";
import "@phosphor-icons/webcomponents/PhCheckCircle";
import "@phosphor-icons/webcomponents/PhXCircle";
import "@phosphor-icons/webcomponents/PhEye";
import "ui-components/button/wow-button";
import "ui-components/status-badge/status-badge";
import "ui-components/modal/wow-modal";
import "ui-components/confirmation/wow-confirmation";

import { styles } from "./live-events-page.css";
import { appContext } from "ui-common/appContext";
import { featureFlagsQueryOptions } from "../../queries/feature-flags";
import { DataQueryController } from "ui-common/data-query-controller";
import { liveEventsQueryOptions } from "../../queries/live-events";
import { rpc } from "ui-common/services/rpc";
import { NotificationService } from "ui-components/notification";
import type { GetLiveEventsResult } from "api-contracts/rpc/get-live-events";

import "../../components/page-content/page-content";
import "../../components/view-primary-buttons/view-primary-buttons";
import "../../components/content-container/content-container";
import "../../components/create-live-event-modal/create-live-event-modal";
import "../../components/edit-live-event-modal/edit-live-event-modal";
import { getAssetUrl } from "helpers/assetHelpers";

type LiveEventFilter = "all" | "upcoming" | "live" | "ended";

@customElement("live-events-page")
export class LiveEventsPage extends LitElement {
  static override styles = [styles];

  @state()
  private selectedFilter: LiveEventFilter = "all";

  @state()
  private showCreateModal = false;

  @state()
  private showEditModal = false;

  @state()
  private editingEventId?: string;

  @state()
  private deletingEventId?: string;

  private eventsQuery = new DataQueryController(this, () => {
    const status =
      this.selectedFilter === "all"
        ? undefined
        : this.selectedFilter === "upcoming"
          ? ("scheduled" as const)
          : (this.selectedFilter as "live" | "ended");
    return liveEventsQueryOptions(status);
  });

  private featureFlagsQuery = new DataQueryController(this, () => featureFlagsQueryOptions);

  private handleFilterChange(filter: LiveEventFilter) {
    this.selectedFilter = filter;
    this.eventsQuery.refetch();
  }

  private handleCreateClick() {
    this.editingEventId = undefined;
    this.showCreateModal = true;
  }

  private handleEditClick(event: GetLiveEventsResult["events"][0]) {
    this.editingEventId = event.id;
    this.showEditModal = true;
  }

  private handleDeleteClick(eventId: string) {
    this.deletingEventId = eventId;
  }

  private handleCancelDelete() {
    this.deletingEventId = undefined;
  }

  private async handleConfirmDelete() {
    if (!this.deletingEventId) return;

    try {
      await rpc("delete-live-event", { event_id: this.deletingEventId });
      NotificationService.success("Event deleted successfully");
      this.deletingEventId = undefined;
      this.eventsQuery.refetch();
    } catch (error) {
      console.error("Error deleting event:", error);
      NotificationService.error("Failed to delete event");
    }
  }

  private handleCreateModalClose() {
    this.showCreateModal = false;
  }

  private handleEditModalClose() {
    this.showEditModal = false;
    this.editingEventId = undefined;
  }

  private handleEventCreated() {
    this.showCreateModal = false;
    this.eventsQuery.refetch();
  }

  private handleEventUpdated() {
    this.showEditModal = false;
    this.editingEventId = undefined;
    this.eventsQuery.refetch();
  }

  private handleEventEnded() {
    this.showEditModal = false;
    this.editingEventId = undefined;
    this.eventsQuery.refetch();
  }

  private handleViewClick() {
    const handle = appContext.currentUser?.handle;
    if (handle) window.open(`/${handle}/live-events`, "_self", "noopener,noreferrer");
  }

  private getComputedStatus(event: GetLiveEventsResult["events"][0]): string {
    return event.status;
  }

  private renderStatusBadge(event: GetLiveEventsResult["events"][0]) {
    const status = this.getComputedStatus(event);

    switch (status) {
      case "scheduled":
        return html`
          <status-badge variant="scheduled" size="medium">
            <ph-calendar slot="icon" size="14" weight="fill"></ph-calendar>
            Scheduled
          </status-badge>
        `;
      case "live":
        return html`
          <status-badge variant="live" size="medium" pulse>
            <ph-circle slot="icon" size="14" weight="fill"></ph-circle>
            Live
          </status-badge>
        `;
      case "ended":
        return html`
          <status-badge variant="ended" size="medium">
            <ph-check-circle slot="icon" size="14" weight="fill"></ph-check-circle>
            Ended
          </status-badge>
        `;
      case "cancelled":
        return html`
          <status-badge variant="cancelled" size="medium">
            <ph-x-circle slot="icon" size="14" weight="fill"></ph-x-circle>
            Cancelled
          </status-badge>
        `;
      default:
        return html`<status-badge size="medium">${status}</status-badge>`;
    }
  }

  private renderMainContent() {
    const featureFlags = this.featureFlagsQuery.result?.data?.features ?? [];
    const hasLiveEvents = featureFlags.includes("paid-live-events") || featureFlags.includes("open-live-events");

    // Show access denied message for non-enabled creators
    if (!hasLiveEvents) {
      return html` <div class="main-content">${this.renderEmptyState()}</div> `;
    }

    // Show loading state if query hasn't completed yet
    if (!this.eventsQuery.result || this.eventsQuery.result.isPending) {
      return html`
        <div class="main-content">
          <div class="loading-state">
            <div>Loading events...</div>
          </div>
        </div>
      `;
    }

    const events = this.eventsQuery.result.data?.events || [];

    return html`
      <div class="main-content">
        ${this.renderFilterButtons()} ${events.length > 0 ? this.renderEventsList(events) : this.renderEmptyState()}
      </div>
    `;
  }

  private renderEventsList(events: GetLiveEventsResult["events"]) {
    return html`
      <div class="events-list">
        ${events.map(
          (event) => html`
            <div class="event-card">
              ${event.thumbnail_url
                ? html`<img class="event-thumbnail" src="${getAssetUrl(event.thumbnail_url)}" alt="${event.title}" />`
                : nothing}
              <div class="event-content">
                <div class="event-header">
                  <h3>${event.title}</h3>
                  <div class="event-header-actions">
                    ${this.renderStatusBadge(event)}
                    <div class="event-actions">
                      ${this.getComputedStatus(event) === "scheduled"
                        ? html`
                            <wow-button
                              variant="subtle"
                              size="small"
                              @click=${() => this.handleEditClick(event)}
                              aria-label="Edit event"
                            >
                              <ph-pencil size="16"></ph-pencil>
                            </wow-button>
                          `
                        : nothing}
                      ${this.getComputedStatus(event) === "ended"
                        ? html`
                            <wow-button
                              variant="subtle"
                              size="small"
                              @click=${() => this.handleEditClick(event)}
                              aria-label="View event"
                            >
                              <ph-eye size="16"></ph-eye>
                            </wow-button>
                          `
                        : nothing}
                      ${this.getComputedStatus(event) === "live"
                        ? html`
                            <wow-button
                              variant="subtle"
                              size="small"
                              @click=${() => this.handleEditClick(event)}
                              aria-label="Edit event"
                            >
                              <ph-pencil size="16"></ph-pencil>
                            </wow-button>
                          `
                        : nothing}
                      ${this.getComputedStatus(event) !== "live"
                        ? html`
                            <wow-button
                              variant="subtle"
                              size="small"
                              @click=${() => this.handleDeleteClick(event.id)}
                              aria-label="Delete event"
                            >
                              <ph-trash size="16"></ph-trash>
                            </wow-button>
                          `
                        : nothing}
                    </div>
                  </div>
                </div>
                ${event.description ? html`<p class="event-description">${event.description}</p>` : nothing}
                <div class="event-meta">
                  <div class="meta-item">
                    <strong>Scheduled:</strong> ${new Date(event.scheduled_start_time).toLocaleString()}
                  </div>
                  ${event.content_tier === "premium"
                    ? html`
                        <div class="meta-item">
                          <strong>Premium Event</strong>
                        </div>
                      `
                    : nothing}
                  ${event.max_attendees
                    ? html` <div class="meta-item"><strong>Max Attendees:</strong> ${event.max_attendees}</div> `
                    : nothing}
                </div>
              </div>
            </div>
          `,
        )}
      </div>
    `;
  }

  private renderFilterButtons() {
    return html`
      <div class="filter-buttons">
        <wow-button
          variant=${this.selectedFilter === "all" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("all")}
        >
          All Events
        </wow-button>
        <wow-button
          variant=${this.selectedFilter === "upcoming" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("upcoming")}
        >
          Upcoming
        </wow-button>
        <wow-button
          variant=${this.selectedFilter === "live" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("live")}
        >
          Live
        </wow-button>
        <wow-button
          variant=${this.selectedFilter === "ended" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("ended")}
        >
          Ended
        </wow-button>
      </div>
    `;
  }

  private renderEmptyState() {
    const featureFlags = this.featureFlagsQuery.result?.data?.features ?? [];
    const hasLiveEvents = featureFlags.includes("paid-live-events") || featureFlags.includes("open-live-events");

    if (!hasLiveEvents) {
      return html`
        <div class="empty-state">
          <ph-video-camera size="64" weight="thin"></ph-video-camera>
          <h2>Live Events Not Available</h2>
          <p>Live Events feature is currently in beta. Contact support if you're interested in early access.</p>
        </div>
      `;
    }

    return html`
      <div class="empty-state">
        <ph-video-camera size="64" weight="thin"></ph-video-camera>
        <h2>No live events yet</h2>
        <p>Create your first live event to start streaming to your audience</p>
        <wow-button variant="primary" @click=${this.handleCreateClick}> Create Live Event </wow-button>
      </div>
    `;
  }

  override render() {
    return html`
      <page-content .pageTitle=${"Live Events"}>
        <div slot="actions">
          <view-primary-buttons
            primaryText="Create Live Event"
            @primary-click=${this.handleCreateClick}
            @view-click=${this.handleViewClick}
          ></view-primary-buttons>
        </div>

        <div class="containers">
          <content-container variant="primary" columns="1">
            <div slot="column" class="live-events-content">${this.renderMainContent()}</div>
          </content-container>
        </div>
      </page-content>

      ${this.showCreateModal
        ? html`
            <create-live-event-modal
              .featureFlags=${this.featureFlagsQuery.result?.data?.features ?? []}
              @modal-close=${this.handleCreateModalClose}
              @event-created=${this.handleEventCreated}
            ></create-live-event-modal>
          `
        : nothing}
      ${this.showEditModal && this.editingEventId
        ? html`
            <edit-live-event-modal
              .eventId=${this.editingEventId}
              .featureFlags=${this.featureFlagsQuery.result?.data?.features ?? []}
              @modal-close=${this.handleEditModalClose}
              @event-updated=${this.handleEventUpdated}
              @event-ended=${this.handleEventEnded}
            ></edit-live-event-modal>
          `
        : nothing}
      ${this.deletingEventId
        ? html`
            <wow-confirmation header="Delete event">
              <div class="delete-confirmation-content">
                <p>Are you sure you want to delete this event? This action cannot be undone.</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmDelete}>
                    Delete event
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelDelete}> Cancel </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-events-page": LiveEventsPage;
  }
}
